
import React from 'react';
import { Class, ClassSchedule } from '@/types/classTypes';
import { Badge } from '@/components/ui/badge';
import { Clock, Calendar, MapPin, Shirt } from 'lucide-react';

interface ClassScheduleDisplayProps {
  schedules: ClassSchedule[];
  classItem: Class;
}

const ClassScheduleDisplay: React.FC<ClassScheduleDisplayProps> = ({ schedules, classItem }) => {
  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  
  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  if (schedules.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p>No schedules have been added for this class yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {schedules.map(schedule => (
        <div key={schedule.id} className="border rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center mb-2">
                <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {schedule.weekday !== undefined ? weekdays[schedule.weekday] : 'Custom Schedule'}
                </span>
              </div>
              
              {(schedule.start_time || schedule.end_time) && (
                <div className="flex items-center mb-2">
                  <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>
                    {schedule.start_time ? formatTime(schedule.start_time) : ''}
                    {schedule.start_time && schedule.end_time ? ' - ' : ''}
                    {schedule.end_time ? formatTime(schedule.end_time) : ''}
                  </span>
                </div>
              )}
              
              {classItem.location && (
                <div className="flex items-center mb-2">
                  <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{classItem.location}</span>
                </div>
              )}
              
              {classItem.dress_code && (
                <div className="flex items-start mb-2">
                  <Shirt className="mr-2 h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="font-medium text-sm">Dress Code:</span>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{classItem.dress_code}</p>
                  </div>
                </div>
              )}
            </div>
            
            {schedule.name && (
              <Badge variant="outline">{schedule.name}</Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ClassScheduleDisplay;
